class ShopTheLookModal extends HTMLElement {
  constructor() {
    super();
    this.modal = this.querySelector('.modal');
    this.closeBtn = this.querySelector('[data-shop-look-close]');
    this.blockId = this.dataset.blockId;

    this.bindEvents();
    this.initVariantSelectors();
  }

  bindEvents() {
    // Close button
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', this.close.bind(this));
    }

    // Close on overlay click
    if (this.modal) {
      this.modal.addEventListener('click', (e) => {
        if (e.target === this.modal || e.target.classList.contains('modal-dialog-centered')) {
          this.close();
        }
      });
    }

    // Escape key
    this.addEventListener('keyup', (e) => {
      if (e.code === 'Escape') this.close();
    });

    // Add All button
    const addAllBtn = this.querySelector('[data-stl-add-all]');
    if (addAllBtn) {
      addAllBtn.addEventListener('click', this.addAllToBag.bind(this));
    }

    // Individual add-to-cart forms are handled by the <product-form> component
    // (assets/component-product-form.js): it posts to /cart/add.js and opens the
    // Rebuy Smart Cart, exactly like the PDP. We must NOT add a second submit
    // handler that also posts to the cart, or the item is added twice. Here we
    // only close this modal on submit so the Smart Cart is visible afterwards.
    this.querySelectorAll('form[data-stl-form]').forEach((form) => {
      form.addEventListener('submit', () => this.close());
    });
  }

  initVariantSelectors() {
    this.querySelectorAll('.stl-variant-selector').forEach((select) => {
      select.addEventListener('change', this.onVariantChange.bind(this));
    });
  }

  onVariantChange(event) {
    const select = event.target;
    const productId = select.dataset.productId;
    const productContainer = this.querySelector(`[data-product-id="${productId}"]`);
    if (!productContainer) return;

    // Collect all selected options for this product
    const selects = productContainer.querySelectorAll('.stl-variant-selector');
    const selectedOptions = [];
    selects.forEach((s) => selectedOptions.push(s.value));

    // Find matching variant from JSON
    const variantScript = this.querySelector(`.stl-variantsJSON-${productId}`);
    if (!variantScript) return;

    let variants;
    try {
      variants = JSON.parse(variantScript.textContent);
    } catch (e) {
      return;
    }

    const matchedVariant = variants.find((v) =>
      v.options.every((opt, i) => opt === selectedOptions[i])
    );

    if (!matchedVariant) return;

    // Update hidden variant ID input
    const variantInput = productContainer.querySelector('[data-set-variant-id]');
    if (variantInput) variantInput.value = matchedVariant.id;

    // Swap product image to the selected variant's image (if it has one)
    if (matchedVariant.featured_image && matchedVariant.featured_image.src) {
      const productImage = productContainer.querySelector('[data-stl-product-image]');
      if (productImage) {
        productImage.srcset = matchedVariant.featured_image.src;
        productImage.src = matchedVariant.featured_image.src;
      }
    }

    // Update price display
    const priceEl = productContainer.querySelector('.price.money');
    const compareEl = productContainer.querySelector('s.money');
    if (priceEl) {
      priceEl.textContent = this.formatMoney(matchedVariant.price);
      if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
        priceEl.classList.add('price-with-compare');
      } else {
        priceEl.classList.remove('price-with-compare');
      }
    }
    if (compareEl) {
      if (matchedVariant.compare_at_price && matchedVariant.compare_at_price > matchedVariant.price) {
        compareEl.textContent = this.formatMoney(matchedVariant.compare_at_price);
        compareEl.style.display = '';
      } else {
        compareEl.style.display = 'none';
      }
    }

    // Update button state
    const submitBtn = productContainer.querySelector('[type="submit"]');
    if (submitBtn) {
      const addText = submitBtn.querySelector('.add-text');
      if (matchedVariant.available) {
        submitBtn.disabled = false;
        if (addText) addText.textContent = 'ADD TO BAG';
      } else {
        submitBtn.disabled = true;
        if (addText) addText.textContent = 'SOLD OUT';
      }
    }
  }

  formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  open() {
    if (!this.modal) return;
    this.modal.classList.add('visible');
    // Force reflow so transition triggers
    this.modal.offsetHeight;
    this.modal.classList.add('open');
    siteOverlay.prototype.showOverlay();
  }

  close() {
    if (!this.modal) return;
    this.modal.classList.remove('open');
    siteOverlay.prototype.hideOverlay();
    this.modal.addEventListener('transitionend', () => {
      if (!this.modal.classList.contains('open')) {
        this.modal.classList.remove('visible');
      }
    }, { once: true });
  }

  // Note: individual "Add to bag" submits are handled by the <product-form>
  // component (Rebuy flow). See bindEvents() — this modal only closes on submit.

  // Mirror the PDP add flow: refresh and reveal the Rebuy Smart Cart.
  // Rebuy listens for `rebuy:cart.add`; we also call its API defensively in
  // case the event isn't bound yet (same approach as component-product-form.js).
  openRebuyCart(parsedState) {
    try {
      document.dispatchEvent(new CustomEvent('rebuy:cart.add', { detail: parsedState }));
      if (window.Rebuy) {
        if (window.Rebuy.Cart && typeof window.Rebuy.Cart.refreshCart === 'function') {
          window.Rebuy.Cart.refreshCart();
        }
        if (window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function') {
          window.Rebuy.SmartCart.show();
        }
      }
    } catch (err) {
      console.error('Shop the Look - Rebuy cart open failed:', err);
    }
  }

  async addAllToBag(event) {
    event.preventDefault();
    const btn = event.currentTarget;
    btn.classList.add('loading');
    btn.disabled = true;

    const items = [];
    this.querySelectorAll('form[data-stl-form]').forEach((form) => {
      const variantInput = form.querySelector('[data-set-variant-id]');
      const submitBtn = form.querySelector('[type="submit"]');
      if (variantInput && submitBtn && !submitBtn.disabled) {
        items.push({
          id: parseInt(variantInput.value),
          quantity: 1,
        });
      }
    });

    if (items.length === 0) {
      btn.classList.remove('loading');
      btn.disabled = false;
      return;
    }

    try {
      const response = await fetch(`${routes.cart_add_url}.js`, {
        ...fetchConfig(),
        body: JSON.stringify({ items }),
      });
      const parsedState = await response.json();

      this.openRebuyCart(parsedState);
      this.close();
    } catch (e) {
      console.error('Shop the Look - Add all error:', e);
    } finally {
      setTimeout(() => {
        btn.classList.remove('loading');
        btn.disabled = false;
      }, 1000);
    }
  }
}

// Prev/next arrows for a card track (Shop the Look and Styled By sections). The track itself is a native
// scroll-snap container (see shop-the-look.css), so swipe/trackpad scrolling works
// without JS; this only moves it one card per click and hides an arrow at its end.
class ShopTheLookSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-stl-track]');
    this.prevBtn = this.querySelector('[data-stl-prev]');
    this.nextBtn = this.querySelector('[data-stl-next]');
    if (!this.track || !this.prevBtn || !this.nextBtn) return;

    this.prevBtn.addEventListener('click', () => this.step(-1));
    this.nextBtn.addEventListener('click', () => this.step(1));
    this.track.addEventListener('scroll', () => this.updateArrows(), { passive: true });
    this.resizeObserver = new ResizeObserver(() => this.updateArrows());
    this.resizeObserver.observe(this.track);
    this.updateArrows();
  }

  disconnectedCallback() {
    this.resizeObserver?.disconnect();
  }

  step(direction) {
    const card = this.track.firstElementChild;
    if (!card) return;
    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.track.scrollBy({ left: direction * (card.offsetWidth + gap), behavior: 'smooth' });
  }

  updateArrows() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    this.prevBtn.disabled = scrollLeft <= 1;
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 1;
    // Everything fits: lets CSS hide the arrows entirely.
    this.toggleAttribute('data-stl-static', this.prevBtn.disabled && this.nextBtn.disabled);
  }
}

if (!customElements.get('shop-the-look-slider')) {
  customElements.define('shop-the-look-slider', ShopTheLookSlider);
}

// Guard against double-definition: the standalone Shop the Look section and an
// STL-enabled collection infocard can both load this script on one page.
if (!customElements.get('shop-the-look-modal')) {
  customElements.define('shop-the-look-modal', ShopTheLookModal);
}

// Bind open buttons to modals.
//
// Delegated from document rather than bound per button: the collection filters
// re-render #collection-product-grid via innerHTML (component-collection-filters.js),
// which destroys the infocard triggers and rebuilds them as fresh nodes. Anything
// bound directly to a button is lost on the first filter change, leaving a visible
// "Shop the Look" card whose click does nothing. Delegation also removes the
// DOMContentLoaded race for deferred/late-loaded copies of this script.
// The modals themselves live outside the grid container, so they survive re-render.
if (!window.shopTheLookTriggersBound) {
  window.shopTheLookTriggersBound = true;
  document.addEventListener('click', (e) => {
    const btn = e.target.closest?.('[data-shop-look-open]');
    if (!btn) return;
    e.preventDefault();
    const modal = document.getElementById(`ShopTheLookModal-${btn.dataset.shopLookOpen}`);
    if (modal && typeof modal.open === 'function') {
      modal.open();
    }
  });
}
